import ForecastCategoryCard from './ForecastCategoryCard.jsx';
import { formatCurrency } from '../../lib/format.js';
import { sumCategoryMonthlyCents, hasAnyForecast } from '../../lib/forecastHelpers.js';

/**
 * Section pour une catégorie racine avec enfants.
 * Affiche le nom en gras + total agrégé puis les ForecastCategoryCard des enfants.
 *
 * Props :
 *   category                — catégorie racine avec children[]
 *   onAddItem               — (categoryId, categoryName, flowType) => void
 *   onEditItem              — (item) => void
 *   onDeleteItem            — (item) => void (optionnel)
 *   onOpenDrawer            — (item) => void
 *   onOpenRecurrenceHistory — (item) => void
 */
export default function ForecastCategorySection({
  category,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onOpenDrawer,
  onOpenRecurrenceHistory,
}) {
  const children = (category.children ?? []).filter(hasAnyForecast);
  const totalCents = sumCategoryMonthlyCents(category);
  const isIncome = category.flow_type === 'income';

  const totalDisplay = totalCents !== 0
    ? (isIncome ? '+' : '') + formatCurrency(Math.abs(totalCents))
    : '—';
  const totalColorClass = totalCents === 0
    ? 'text-slate-400'
    : isIncome
    ? 'text-green-600'
    : 'text-red-600';

  const sharedProps = {
    onAddItem,
    onEditItem,
    onDeleteItem,
    onOpenDrawer,
    onOpenRecurrenceHistory,
  };

  return (
    <div>
      {/* En-tête racine */}
      <div className="flex items-center gap-2 px-1 py-1.5 mb-2">
        <span className="flex-1 text-sm font-bold text-slate-600 truncate">
          {category.name}
        </span>
        <span className={`shrink-0 text-xs font-bold tabular-nums ${totalColorClass}`}>
          {totalDisplay}
          {totalCents !== 0 && (
            <span className="text-[10px] font-normal text-slate-400 ml-0.5">/mois</span>
          )}
        </span>
      </div>

      {/* Si la racine a aussi des items directs (cas rare), carte propre pour la racine */}
      {(category.items?.length ?? 0) > 0 && (
        <div className="pl-2 mb-2">
          <ForecastCategoryCard
            category={category}
            {...sharedProps}
          />
        </div>
      )}

      {/* Cards des enfants — récursif : si l'enfant a lui-même des enfants,
          on rend une sous-section indentée pour ne pas perdre les items des
          niveaux profonds (ex: Marketing > Campagnes > Facebook Ads). */}
      <div className="pl-2 space-y-2">
        {children.map((child) =>
          (child.children?.length ?? 0) > 0 ? (
            <ForecastCategorySection
              key={child.id}
              category={child}
              {...sharedProps}
            />
          ) : (
            <ForecastCategoryCard
              key={child.id}
              category={child}
              {...sharedProps}
            />
          )
        )}
      </div>
    </div>
  );
}
